import BpkBadge, {
  BADGE_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-badge';
import {
  BpkCardV2,
  CARD_V2_SURFACE_COLORS,
  CARD_V2_VARIANTS,
} from '@skyscanner-internal/backpack-web/bpk-component-card';
import AiIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/ai';
import ChevronRightIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/chevron-right';
import BpkText, {
  TEXT_STYLES,
  TEXT_COLORS,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import classNames from 'classnames';

import withErrorBoundary from '../ErrorBoundary';
import { useMBDContext } from '../common/MBD/useMBDContext';

import type {
  CarHireAirportVsCityChartLocation,
  CarHireAirportVsCityChartProps,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/CarHireAirportVsCityChartProps';

import STYLES from './CarHireAirportVsCityChart.module.scss';

const getRatio = (price: number, maxPrice: number) => {
  if (!maxPrice || price > maxPrice) {
    return 100;
  }
  return (price / maxPrice) * 100;
};

const Line = ({
  buildMBDParam,
  isPrimary,
  location,
  maxPrice,
  mbdComponentName,
}: {
  isPrimary: boolean;
  location: CarHireAirportVsCityChartLocation;
  maxPrice: number;
  mbdComponentName: string;
  buildMBDParam: (componentName: string) => string;
}) => {
  const { formattedPrice, name, price, url } = location;
  const ratio = getRatio(price, maxPrice);
  const labelStyle = isPrimary ? STYLES.Label_primary : STYLES.Label_secondary;
  const labelTextColor = isPrimary
    ? TEXT_COLORS.textOnDark
    : TEXT_COLORS.textPrimary;

  return (
    <a
      href={url}
      rel="nofollow"
      className={STYLES.Line}
      data-tracking-element-id={mbdComponentName}
      data-tracking-common-params={buildMBDParam(mbdComponentName)}
    >
      <div className={STYLES.Track}>
        <div className={labelStyle} style={{ width: `${ratio}%` }}>
          <BpkText
            textStyle={TEXT_STYLES.label2}
            color={labelTextColor}
            className={STYLES.Label__text}
          >
            {name}
          </BpkText>
        </div>
      </div>
      <BpkBadge
        type={isPrimary ? BADGE_TYPES.brand : BADGE_TYPES.strong}
        className={STYLES.Badge}
      >
        {formattedPrice}
        <ChevronRightIcon className={STYLES.Badge__chevron} />
      </BpkBadge>
    </a>
  );
};

const SummaryStat = ({ label, value }: { label: string; value: string }) => (
  <div className={STYLES.SummaryStat}>
    <BpkText textStyle={TEXT_STYLES.caption} color={TEXT_COLORS.textSecondary}>
      {label}
    </BpkText>
    <BpkText textStyle={TEXT_STYLES.label1}>{value}</BpkText>
  </div>
);

const CarHireAirportVsCityChart = (props: CarHireAirportVsCityChartProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();

  if (!props.city || !props.airport) {
    return null;
  }

  const {
    airport,
    city,
    description,
    headline,
    isMobile,
    rtl,
    summaryStats,
    summaryTag,
    summaryTitle,
  } = props;

  const maxPrice = Math.max(city.price, airport.price);
  const priceBars = [
    {
      id: 'city',
      location: city,
    },
    {
      id: 'airport',
      location: airport,
    },
  ].sort((first, second) => first.location.price - second.location.price);

  const className = classNames(STYLES.CarHireAirportVsCityChart, {
    [STYLES.rtl]: rtl,
  });

  return (
    <div
      className={className}
      data-testid="carHireAirportVsCityChart"
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName)}
    >
      <div className={STYLES.Intro}>
        <BpkText
          tagName="h2"
          textStyle={isMobile ? TEXT_STYLES.heading3 : TEXT_STYLES.heading2}
        >
          {headline}
        </BpkText>
        <BpkText textStyle={TEXT_STYLES.bodyDefault}>{description}</BpkText>
      </div>
      <div className={STYLES.Row}>
        <div className={STYLES.Bars}>
          {priceBars.map(({ id, location }, index) => (
            <Line
              key={id}
              location={location}
              maxPrice={maxPrice}
              isPrimary={index === 0}
              mbdComponentName={`${rawComponentName}_${id}`}
              buildMBDParam={buildMBDParam}
            />
          ))}
        </div>
        <div className={STYLES.Summary}>
          <BpkCardV2.Root
            bgColor={CARD_V2_SURFACE_COLORS.surfaceLowContrast}
            variant={CARD_V2_VARIANTS.noElevation}
          >
            <BpkCardV2.Body>
              <div className={STYLES.SummaryTagRow}>
                <AiIcon className={STYLES.SummaryTagIcon} />
                <BpkText
                  textStyle={TEXT_STYLES.caption}
                  color={TEXT_COLORS.textSecondary}
                  className={STYLES.SummaryTagText}
                >
                  {summaryTag}
                </BpkText>
              </div>
              <BpkText
                textStyle={TEXT_STYLES.heading5}
                className={STYLES.SummaryTitle}
              >
                {summaryTitle}
              </BpkText>
              <div className={STYLES.SummaryStats}>
                {summaryStats.map(({ label, value }) => (
                  <SummaryStat key={label} label={label} value={value} />
                ))}
              </div>
            </BpkCardV2.Body>
          </BpkCardV2.Root>
        </div>
      </div>
    </div>
  );
};

const CarHireAirportVsCityChartWithErrorBoundary = withErrorBoundary(
  CarHireAirportVsCityChart,
  'car-hire-airport-vs-city-chart-component',
);

export default CarHireAirportVsCityChartWithErrorBoundary;
